import React, { useRef, useState, useCallback, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faEllipsisV,
  faStar,
  faBan,
  faTrash,
  faTags,
} from "@fortawesome/free-solid-svg-icons";
import LabelBadge from './LabelBadge';
import StrategyBadge from './StrategyBadge';
import LabelDropdown from './LabelDropdown';
import { FiChevronDown, FiChevronUp, FiZap } from "react-icons/fi";
import Favicon from "./Favicon";
import AiAnalysisPanel from "./AiAnalysisPanel";
import CallHighlights, { Pill } from "./earnings/CallHighlights";
import MoverExplanation from "./MoverExplanation";
import { indicatorText } from "../utils/moverNews";
import { GUIDANCE_STYLE } from "../shared/callHighlightStyles";
import EditableNotes from "./EditableNotes";
import "./CompanyRow.css";
import {
  formatDate,
  daysUntilEarnings,
  getChangeClass,
  formatChange,
  formatSignedPercent,
  getRowStyle,
  formatNumberAbbreviated, // Import it from utils.js
  formatMarketCap, // Import if you're using it
} from "./utils";
import { lastReportSummary, formatEventDate, easternSessionDate } from "../utils/earningsElapsed";
import EarningsBadge from "./EarningsBadge";
import TechnicalSignalIcons from './TechnicalSignalIcons';
import CONFIG from '../config/config';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import { publicFetch } from '../utils/authenticatedFetch';
import { useSignalWatches } from '../services/api';
import { strategyBadgeFor } from '../utils/strategyBadge';
import ExpectationsBarMeter from './ExpectationsBarMeter';
import AnalystRatingText from './AnalystRatingText';
import { rangePositionScore } from '../shared/earningsSetup';
import { TEXT_MUTED } from '../constants/uiColors';
import { Tooltip } from "react-tooltip";

// Both numbers describe the SAME month: the most recent one with a prior-year
// counterpart. This used to show the newest COLLECTED month's visits beside a
// year-over-year percentage measured on a different month — invisible while
// trafficChange was always null (issue #122), and wrong the moment it wasn't.
const TrafficCell = ({ trafficComparableVisits, trafficComparablePeriod, trafficChange }) => {
  if (trafficComparableVisits == null || trafficChange == null) {
    return <div></div>;
  }

  return (
    <div title={trafficComparablePeriod ? `${trafficComparablePeriod} vs a year earlier` : undefined}>
      {formatNumberAbbreviated(trafficComparableVisits)}{" "}
      <span className={getChangeClass(trafficChange)}>
        {formatChange(trafficChange)}
      </span>
    </div>
  );
};



// One implementation of the "current price + a percent change" cell body, which
// was copy-pasted six times below (stockPrice and the five priceChangeNd
// columns) with only the field name varying. `currency` is the single real
// difference between the variants: the Price column prefixes a $, the change
// columns do not.
//
// The change renders only when it is a usable number — an unusable one drops
// out entirely rather than painting a red "N/A", since getChangeClass(NaN)
// resolves to the negative colour and would state a direction we do not have.
const renderPriceWithChange = (price, change, { currency = false } = {}) => (
  <>
    {price != null && !isNaN(price)
      ? `${currency ? '$' : ''}${parseFloat(price).toFixed(2)} `
      : ''}
    {change != null && !isNaN(change) && (
      <span className={getChangeClass(change)}>{formatChange(change)}</span>
    )}
  </>
);

// The muted elapsed chip in the Since Earnings cell. Inline for the same reason
// ExpectationsBarMeter's styles are (ml-1, text-gray-400/500 and
// whitespace-nowrap are absent from the prebuilt tailwind subset).
const ELAPSED_CHIP_STYLE = {
  marginLeft: '0.35em',
  color: TEXT_MUTED,
  fontSize: '0.75em',
  whiteSpace: 'nowrap',
};

const CompanyRow = ({
  company,
  callHighlights,
  moverNews = null,
  expandedRow,
  toggleRow,
  onAddToWatchList,
  onDelete,
  onNotInterested,
  dateColumnStyle,
  selectedTab,
  listName = '',
  colorSettings,
  columns,
  news = [],
  onNoteChange,
  // Labels props
  labelsData = { labels: [], assignments: {} },
  userEmail,
  onOpenLabelManager,
  onOpenPurchaseInfo,
}) => {
  const rowStyle = getRowStyle(company, colorSettings);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [showLabelsSubmenu, setShowLabelsSubmenu] = useState(false);

  const [hasNews, setHasNews] = useState(false);

  // Strategy badge (issue #411): the strategy watching this stock, flagged once its
  // fit is due for review. One shared, cached query across all rows.
  const { data: signalWatchData } = useSignalWatches(!!userEmail, userEmail);
  const strategyBadge = strategyBadgeFor(
    signalWatchData?.watches?.find((w) => w.ticker === company.tickerSymbol?.toUpperCase()),
    signalWatchData?.strategies
  );

  // Get labels assigned to this stock
  const stockAssignment = labelsData.assignments?.[company.tickerSymbol] || { labels: [], purchases: {} };
  const assignedLabels = stockAssignment.labels
    ?.map(labelId => labelsData.labels.find(l => l.id === labelId))
    .filter(Boolean) || [];
  const [relevantNews, setRelevantNews] = useState([]);

  // Replace API call with local filtering
  useEffect(() => {
    if (!news?.length || !company?.tickerSymbol) {
      setHasNews(false);
      setRelevantNews([]);
      return;
    }

    const companySymbol = company.tickerSymbol.toUpperCase();
    const filteredNews = news.filter(article => 
      article.tickers?.some(ticker => 
        ticker.replace(/[$#]/g, '').toUpperCase() === companySymbol
      )
    ).slice(0, 5); // Keep top 5 most recent articles

    setHasNews(filteredNews.length > 0);
    setRelevantNews(filteredNews);
  }, [news, company.tickerSymbol]);

  const handleDropdownToggle = useCallback(
    (e) => {
      e.stopPropagation();
      setIsDropdownOpen((prev) => !prev);
    },
    []
  );

  // Add click outside handler to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Reset labels submenu when main dropdown closes
  useEffect(() => {
    if (!isDropdownOpen) {
      setShowLabelsSubmenu(false);
    }
  }, [isDropdownOpen]);

  const handleAddToWatchList = useCallback(
    (e) => {
      e.stopPropagation();
      onAddToWatchList(company);

      // Add a note indicating where the symbol was added from
      if (onNoteChange && selectedTab && selectedTab !== 'watchlist') {
        const dateStr = new Date().toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });
        const displayName = listName || selectedTab;
        const sourceNote = `Added from "${displayName}" on ${dateStr}`;
        onNoteChange(company.tickerSymbol, sourceNote);
      }

      setIsDropdownOpen(false);
    },
    [company, onAddToWatchList, onNoteChange, selectedTab, listName]
  );

  const handleNotInterested = useCallback(
    (e) => {
      e.stopPropagation();
      if (onNotInterested) {
        onNotInterested(company.tickerSymbol);
        setIsDropdownOpen(false);
      }
    },
    [company, onNotInterested]
  );

  const handleDelete = useCallback(
    async (e) => {
      e.stopPropagation();
      try {
        await onDelete(company.tickerSymbol);
      } catch (error) {
        console.error("Error deleting company:", error);
      }
      setIsDropdownOpen(false);
    },
    [company.tickerSymbol, onDelete]
  );

  const renderLogo = () => {
    if (company.logo_url) {
      return (
        <img
          src={company.logo_url}
          alt={`${company.name} logo`}
          className="w-6 h-6 mr-2"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
              company.name
            )}&background=random`;
          }}
        />
      );
    } else {
      return (
        <div className="w-6 h-6 mr-2 bg-gray-200 flex items-center justify-center rounded-full">
          {company.name.charAt(0)}
        </div>
      );
    }
  };

  const iconStyle = {
    width: "16px",
    height: "16px",
    color: "#6B7280", // Tailwind's gray-500
  };

  // Function to render cell content based on column key
  const renderCellContent = (column) => {
    const { key, format } = column;

    // Add debug logging
    //console.log('Rendering cell for column:', key, 'Company data:', company);

    switch (key) {
      case "date":
        if (!company.earningsDate) {
          console.log('Missing earnings date for:', company.tickerSymbol);
        }
        return (
          <div className="text-sm">
            {formatDate(company.earningsDate)}
            <br />
            <EarningsBadge
              date={company.earningsDate}
              isEstimated={company.isEarningsDateEstimated}
            />
          </div>
        );

      case "name": {
        const moverTip = indicatorText(moverNews);
        return (
          <div className="flex items-center">
            {renderLogo()}
            <div className="min-w-0 flex-1">
              <strong className="truncate" style={{ display: 'block' }}>
                {company.name}{" "}
                <span className="text-gray-500">
                  ({company.tickerSymbol})
                  {hasNews && (
                    <span className="news-indicator ml-1" data-tooltip-id="news-tooltip" />
                  )}
                  {moverTip && (
                    <span
                      className="mover-indicator"
                      data-tooltip-id="mover-tooltip"
                      data-tooltip-content={moverTip}
                    >
                      <FiZap />
                    </span>
                  )}
                </span>
              </strong>
              {/* Strategy + label badges, always on their own line under the name (issue #411).
                  Inline gap: gap-1 is absent from the prebuilt Tailwind. */}
              {(strategyBadge || assignedLabels.length > 0) && (
                <div className="flex flex-wrap" style={{ gap: 4, marginTop: 2 }}>
                  <StrategyBadge badge={strategyBadge} />
                  {assignedLabels.slice(0, 3).map(label => (
                    <LabelBadge key={label.id} label={label} size="sm" />
                  ))}
                  {assignedLabels.length > 3 && (
                    <span className="text-xs text-gray-400">+{assignedLabels.length - 3}</span>
                  )}
                </div>
              )}
              {company.website && (
                <a
                  href={company.website.startsWith("http") ? company.website : `https://${company.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 underline text-sm"
                >
                  {company.website}
                </a>
              )}
            </div>
          </div>
        );
      }

      // Since Earnings: the drift from the close AFTER the last report, plus
      // how long ago that report was (issue #229). The current price used to
      // lead this cell, duplicating the adjacent Price column and reading as
      // though the price itself were the since-earnings figure; the elapsed
      // count is a far better use of the width, because +5% three sessions
      // after a print and +5% four months after one are different statements.
      //
      // The hover text carries the REACTION — the move on the report itself.
      // That is a DIFFERENT number from the cell's: reaction is
      // (after - before)/before, the cell is (live quote - after)/after. Both
      // come off the same earnings_history event row via
      // workers/shared/earningsReaction.js, so they always describe one quarter.
      case "stockChange": {
        if (company.stockChange == null || isNaN(company.stockChange)) return "";

        // Date, elapsed count and the move on the day come from ONE reading of
        // the last report, shared with the stock profile's Last Report tile
        // (issue #445); see lastReportSummary for why the date is derived from
        // the elapsed count rather than beside it (issue #231).
        const { elapsed, reportedOn, reaction } = lastReportSummary({
          reactionDate: company.earningsReactionDate,
          reactionPercent: company.earningsReactionPercent,
        });
        const hoverParts = [];
        if (reportedOn) hoverParts.push(`Reported ${reportedOn}`);
        if (reaction !== null) hoverParts.push(`${formatSignedPercent(reaction)} on the day`);

        return (
          <span title={hoverParts.length ? hoverParts.join(' \u00b7 ') : undefined}>
            <span className={getChangeClass(company.stockChange)}>
              {formatChange(company.stockChange)}
            </span>
            {/* Absent date -> no chip at all. Never a "0 bars" invented from
                a missing value. */}
            {elapsed && <span style={ELAPSED_CHIP_STYLE}>{elapsed}</span>}
          </span>
        );
      }

      // Guidance direction from the newest analysed earnings call (issue #306). No elapsed chip:
      // guidance is quarterly and the Date column already says when the next report lands, so the
      // call date lives in the hover. No entry means no call on record and renders blank, which
      // must read differently from a call that gave no guidance — that one says so.
      case "guidance": {
        const guidance = company.guidance;
        if (!guidance) return "";

        const style = GUIDANCE_STYLE[guidance.direction];
        // callDate is a full UTC timestamp; its session day is the ET date, not the UTC prefix.
        const callOn = formatEventDate(easternSessionDate(guidance.callDate));
        const hoverParts = [];
        if (guidance.detail) hoverParts.push(guidance.detail);
        else if (guidance.locked) hoverParts.push('The guidance detail and full call summary are a Pro feature');
        if (callOn) hoverParts.push(`Call ${callOn}`);

        return (
          <span title={hoverParts.length ? hoverParts.join(' · ') : undefined}>
            {style
              ? <Pill bg={style.bg} fg={style.fg}>{style.short}</Pill>
              : <span style={{ color: TEXT_MUTED }}>None given</span>}
          </span>
        );
      }

      case "stockPrice":
        return renderPriceWithChange(
          company.stockPrice,
          company.regularMarketChangePercent,
          { currency: true },
        );

      case "trafficChange":
        return (
          <TrafficCell
            trafficComparableVisits={company.trafficComparableVisits}
            trafficComparablePeriod={company.trafficComparablePeriod}
            trafficChange={company.trafficChange}
          />
        );

      case "positionsPercentage":
        return (
          <>
            {company.positions}
            {company.positionsPercentage !== null && (
              <span className={getChangeClass(company.positionsPercentage)}>
                {` ${formatChange(company.positionsPercentage)}`}
              </span>
            )}
          </>
        );

      case "outlook":
        const outlookValue = company.outlook;
        if (outlookValue == null || isNaN(outlookValue)) {
          return "";
        }
        const outlookDisplay = parseFloat(outlookValue).toFixed(2);
        // Show delta only if available (not null)
        if (company.outlookDelta != null) {
          const deltaPercent = (company.outlookDelta * 100).toFixed(0);
          return (
            <>
              {outlookDisplay}{" "}
              <span className={getChangeClass(company.outlookDelta)}>
                {company.outlookDelta >= 0 ? `+${deltaPercent}%` : `${deltaPercent}%`}
              </span>
            </>
          );
        }
        return outlookDisplay;

      case "dividendYield":
        if (company.dividendYield == null || isNaN(company.dividendYield)) {
          return "";
        }
        return `${company.dividendYield.toFixed(2)}%`; // No multiplication

      case "marketCap":
        if (company.marketCap == null || isNaN(company.marketCap)) {
          return "";
        }
        return formatMarketCap(company.marketCap);

      case "actions":
        // Render dropdown menu and expand/collapse icon
        const isExpanded = expandedRow === company.tickerSymbol;
        return (
          <div className="flex items-center gap-1">
            <div className="relative company-dropdown" ref={dropdownRef}>
              <button
                className={`company-dropdown-button text-gray-600 hover:text-gray-800 focus:outline-none p-1 transition-colors duration-200 ease-in-out ${
                  isDropdownOpen ? "bg-gray-200" : ""
                }`}
                type="button"
                onClick={handleDropdownToggle}
                aria-label="Company options"
              >
                <FontAwesomeIcon icon={faEllipsisV} className="w-4 h-4" />
              </button>
              {isDropdownOpen && (
                <div className="company-dropdown-menu">
                  {!showLabelsSubmenu ? (
                    <>
                      {selectedTab !== "watchlist" && (
                        <>
                          <button
                            className="company-dropdown-item watchlist"
                            onClick={handleAddToWatchList}
                          >
                            <FontAwesomeIcon icon={faStar} className="icon text-yellow-500" />
                            Add to Watch List
                          </button>
                          <button
                            className="company-dropdown-item not-interested"
                            onClick={handleNotInterested}
                          >
                            <FontAwesomeIcon icon={faBan} className="icon text-red-500" />
                            Not Interested
                          </button>
                        </>
                      )}
                      {selectedTab === "watchlist" && (
                        <button
                          className="company-dropdown-item delete"
                          onClick={handleDelete}
                        >
                          <FontAwesomeIcon icon={faTrash} className="icon text-red-500" />
                          Delete
                        </button>
                      )}
                      {userEmail && (
                        <button
                          className="company-dropdown-item"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowLabelsSubmenu(true);
                          }}
                        >
                          <FontAwesomeIcon icon={faTags} className="icon text-blue-500" />
                          Labels
                          <span className="ml-auto text-gray-400">›</span>
                        </button>
                      )}
                    </>
                  ) : (
                    <div onClick={(e) => e.stopPropagation()}>
                      <button
                        className="company-dropdown-item text-gray-500 border-b border-gray-100 mb-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowLabelsSubmenu(false);
                        }}
                      >
                        <span className="mr-1">‹</span> Back
                      </button>
                      <LabelDropdown
                        symbol={company.tickerSymbol}
                        userEmail={userEmail}
                        onCreateLabel={() => {
                          setIsDropdownOpen(false);
                          onOpenLabelManager?.();
                        }}
                        onPurchaseInfo={(symbol, label) => {
                          setIsDropdownOpen(false);
                          onOpenPurchaseInfo?.(symbol, label, company.stockPrice);
                        }}
                        onClose={() => setIsDropdownOpen(false)}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleRow(company.tickerSymbol);
              }}
              className="text-gray-600 hover:text-gray-800 focus:outline-none p-1"
              aria-label="Toggle Row Expansion"
            >
              {isExpanded ? (
                <FiChevronUp style={{ width: "16px", height: "16px" }} />
              ) : (
                <FiChevronDown style={{ width: "16px", height: "16px" }} />
              )}
            </button>
          </div>
        );

      case "growth":
        if (company.revenueGrowth == null || isNaN(company.revenueGrowth)) {
          return "";
        }
        return `${(company.revenueGrowth * 100).toFixed(2)}%`;

      case "shortFloat":
        if (company.shortPercentOfFloat == null || isNaN(company.shortPercentOfFloat)) {
          return "";
        }
        return `${(company.shortPercentOfFloat * 100).toFixed(2)}%`;

      case "margins":
        if (company.grossMargins == null || company.operatingMargins == null) {
          return "";
        }
        return (
          <span>
            {(company.grossMargins * 100).toFixed(1)}%{" "}
            <span className={getChangeClass(company.operatingMargins)}>
              {formatChange(company.operatingMargins * 100)}
            </span>
          </span>
        );

      case "beta":
        if (company.beta == null || isNaN(company.beta)) {
          return "";
        }
        return company.beta.toFixed(2);

      case "alpha_12m":
        if (company.alpha_12m == null || isNaN(company.alpha_12m)) {
          return "";
        }
        const alphaValue = company.alpha_12m;
        const alphaColor = alphaValue >= 0 ? '#22c55e' : '#ef4444';
        return (
          <span style={{ color: alphaColor, fontWeight: 500 }}>
            {alphaValue >= 0 ? '+' : ''}{alphaValue.toFixed(1)}%
          </span>
        );

      case "priceChange3d":
        return renderPriceWithChange(company.stockPrice, company.priceChange3d);

      case "priceChange7d":
        return renderPriceWithChange(company.stockPrice, company.priceChange7d);

      case "priceChange14d":
        return renderPriceWithChange(company.stockPrice, company.priceChange14d);

      case "priceChange1m":
        return renderPriceWithChange(company.stockPrice, company.priceChange1m);

      case "priceChange3m":
        return renderPriceWithChange(company.stockPrice, company.priceChange3m);

      case "pegRatio":
        if (company.pegRatio == null || isNaN(company.pegRatio)) {
          return "";
        }
        return company.pegRatio.toFixed(2);

      case "technicalSignals":
        return <TechnicalSignalIcons signals={company.technicalSignals} />;

      case 'extendedHours':
        // If there's a preMarketPrice, show it, else if there's a postMarketPrice, show that
        if (company.preMarketPrice) {
          return (
            <>
              {company.preMarketPrice}{" "}
              <span className={getChangeClass(company.preMarketChangePercent)}>
                {formatChange(company.preMarketChangePercent)}
              </span>
            </>
          );
        } else if (company.postMarketPrice) {
          return (
            <>
              {company.postMarketPrice}{" "}
              <span className={getChangeClass(company.postMarketChangePercent)}>
                {formatChange(company.postMarketChangePercent)}
              </span>
            </>
          );
        } else {
          return "";
        }

      case "industry":
        return company.industry || "";

      case "analystRating":
        return <AnalystRatingText rating={company.averageAnalystRating} />;

      case "fiftyTwoWeekRange": {
        // Same definition as the stock profile's 52W pill and the Expectations
        // Bar's range component (clamped 0–100), so the two can't disagree.
        const rangePosition = rangePositionScore(company.stockPrice, company.fiftyTwoWeekLow, company.fiftyTwoWeekHigh);
        return rangePosition === null ? "" : `${rangePosition.toFixed(0)}%`;
      }

      case "expectationsBar":
        return (
          <ExpectationsBarMeter
            score={company.expectationsBar}
            rating={company.expectationsBarRating}
            coverage={company.expectationsBarCoverage}
            impliedMove={company.impliedMovePct}
          />
        );

      case "notes":
        // Only show notes in watchlist
        if (selectedTab !== 'watchlist') {
          return (
            <div className="text-sm text-gray-400 italic">
              (Only available in watchlist)
            </div>
          );
        }
        return (
          <EditableNotes
            tickerSymbol={company.tickerSymbol}
            initialValue={company.notes || ""}
            onNoteChange={onNoteChange}
          />
        );

      default:
        let value = company[key];
        if (value != null && typeof format === 'function') {
          return format(value) ?? "";
        }
        return value != null ? value : "";
    }
  };

  return (
    <>
      <tr
        key={`${company.tickerSymbol}-main`}
        // Scroll anchor: StockTable finds this row by ticker to focus it
        // (e.g. clicking a symbol in the Signal Watches widget).
        data-ticker={company.tickerSymbol}
        className="hover:bg-gray-50 cursor-pointer"
        style={rowStyle}
        onClick={() => toggleRow(company.tickerSymbol)}
      >
        {columns.map((column) => (
          <td
            key={column.key}
            className="py-2 px-4 border-b"
            style={column.key === 'date' ? dateColumnStyle : {}}
            data-column={column.key}
          >
            {renderCellContent(column)}
          </td>
        ))}
      </tr>
      {expandedRow === company.tickerSymbol && (
        <tr key={`${company.tickerSymbol}-expanded`}>
          <td colSpan={columns.length} className="p-4 bg-gray-100">
            <MoverExplanation explanation={moverNews} />
            {hasNews && (
              <div className="company-news-container mb-4">
                <div className="company-news-content">
                  <h3 className="company-news-header">Recent News</h3>
                  <div>
                    {relevantNews.map((article) => (
                      <a 
                        key={article.url}
                        href={article.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="company-news-item"
                      >
                        {article.imageUrl && (
                          <img
                            src={article.imageUrl}
                            alt={article.title || 'News thumbnail'}
                            className="company-news-image"
                          />
                        )}
                        <div className="company-news-text">
                          <h4 className="company-news-title line-clamp-1">
                            {article.title}
                          </h4>
                          <div className="company-news-meta">
                            {article.source} • {new Date(article.publishedAt).toLocaleDateString()}
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {callHighlights && (
              <div style={{ marginBottom: 16 }}>
                <CallHighlights
                  highlights={callHighlights}
                  reaction={{
                    percent: company.earningsReactionPercent,
                    date: company.earningsReactionDate,
                  }}
                />
              </div>
            )}
            <AiAnalysisPanel
              aiAnalysis={company.ai_analysis}
              stock={company}
            />
          </td>
        </tr>
      )}
    </>
  );
};

// The hover tooltips the row's markers point at (data-tooltip-id). Mounted ONCE
// by the table, never per row: every <Tooltip id> answers every anchor carrying
// that id, so a per-row mount opened one stacked copy per row on each hover —
// 140 at once on the watchlist (issue #411).
//
// The wrapper undoes the desktop `body { zoom: 0.9 }`. react-tooltip measures the
// anchor in zoomed px and writes a position that the body then zooms AGAIN, so
// every tooltip landed ~115px above and left of its marker (measured: rendered
// position = 0.9 x intended). With the zoom cancelled, written px = drawn px.
const WIDE_TIP = { maxWidth: 360, whiteSpace: 'normal' };
const TIP_PROPS = { place: 'top', className: 'z-[9999]', delayHide: 0, positionStrategy: 'fixed' };
const bodyZoom = () => parseFloat(getComputedStyle(document.body).zoom) || 1;
export const CompanyRowTooltips = () => (
  <div style={{ zoom: 1 / bodyZoom() }}>
    <Tooltip id="news-tooltip" content="News available" {...TIP_PROPS} />
    <Tooltip id="strategy-tooltip" style={WIDE_TIP} {...TIP_PROPS} />
    <Tooltip id="mover-tooltip" style={WIDE_TIP} {...TIP_PROPS} />
  </div>
);

export default CompanyRow;
